import React from 'react'
import { Modal, Highlight, ContextNotification } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'
import { mapStatus, normDate } from './dayReservableAreas.helpers'
import {
  findActiveReservation,
  findFutureReservation,
  areaDisplayId,
  formatDate,
} from './uiSelectors'
import { DayReservableAreaReservationForm } from './DayReservableAreaReservationForm'
import { DayReservableAreaSuccess } from './DayReservableAreaSuccess'
import { DayReservableAreaTerminateForm } from './DayReservableAreaTerminateForm'
import { pickResidentDetails } from '../../utils/residentDetails'

export interface DayReservableAreaModalProps {
  area: CoreDayReservableArea | null
  onClose: () => void
}

const hasTerminableReservation = (area: CoreDayReservableArea | null) => {
  if (!area) return false
  const active = findActiveReservation(area)
  const future = findFutureReservation(area)
  const reservation = active || future
  return Boolean(reservation && !reservation.endDate)
}

export const DayReservableAreaModal: React.FC<DayReservableAreaModalProps> = ({
  area,
  onClose,
}) => {
  const { t, i18n } = useTranslation(['common', 'storage'])
  const lang = i18n.language

  const computedStatus = area ? mapStatus(area) : undefined
  const [successFrom, setSuccessFrom] = React.useState<string | null>(null)

  React.useEffect(() => {
    setSuccessFrom(null)
  }, [area?.rentableHierarchyId])

  const showTerminate = hasTerminableReservation(area)

  const currentReservation = React.useMemo(() => {
    if (!area) return null
    const active = findActiveReservation(area)
    if (active) return active
    return findFutureReservation(area) ?? null
  }, [area])

  const scheduledEndISO = React.useMemo(() => {
    return currentReservation?.endDate ?? null
  }, [currentReservation])

  const scheduledEndText = React.useMemo(() => {
    const d = normDate(scheduledEndISO ?? null)
    return d ? formatDate(d, lang) : scheduledEndISO
  }, [scheduledEndISO, lang])

  const noSuccess = !successFrom
  const hasScheduledEnd = !!scheduledEndISO

  const blockedByScheduledEnd = hasScheduledEnd

  const shouldShowCancelledInfo = noSuccess && blockedByScheduledEnd
  const shouldShowTerminateForm = noSuccess && !hasScheduledEnd && showTerminate
  const shouldShowCreateForm =
    noSuccess &&
    !showTerminate &&
    computedStatus === 'default' &&
    !blockedByScheduledEnd

  const displayId = area ? areaDisplayId(area) : ''
  const headerTitle =
    computedStatus === 'default'
      ? t('storage:view.reserve')
      : t('storage:view.details')
  const show = !!area

  const residentDetails = area ? pickResidentDetails(area, lang) : null

  return (
    computedStatus !== undefined &&
    !['disabled', 'not-in-use'].includes(computedStatus) && (
      <Modal
        show={show}
        onClose={onClose}
        id={
          area
            ? `day-reservable-area-${area.rentableHierarchyId ?? 'id'}`
            : 'day-reservable-area'
        }
        title={headerTitle}
        width="m"
        headerElement={<Highlight>{t('storage:view.storageUnit')}</Highlight>}
      >
        {area && (
          <div className="space-y-5">
            {successFrom && (
              <>
                <DayReservableAreaSuccess area={area} fromISO={successFrom} />
                <Modal.Buttons>
                  <Button variant="cancel" onClick={onClose}>
                    {t('common:closeButton')}
                  </Button>
                </Modal.Buttons>
              </>
            )}

            {shouldShowCancelledInfo && (
              <>
                <Modal.Notifications>
                  <ContextNotification type="success">
                    <div className="font-semibold">
                      {t('storage:terminate.cancelledTitle')}
                    </div>
                    <div>
                      {t('storage:terminate.cancelledFrom', {
                        area: displayId,
                        date: scheduledEndText,
                      })}
                    </div>
                  </ContextNotification>
                </Modal.Notifications>
                <Modal.Buttons>
                  <Button variant="cancel" onClick={onClose}>
                    {t('common:closeButton')}
                  </Button>
                </Modal.Buttons>
              </>
            )}
            {shouldShowTerminateForm && (
              <DayReservableAreaTerminateForm area={area} onClose={onClose} />
            )}
            {shouldShowCreateForm && (
              <DayReservableAreaReservationForm
                area={area}
                onSuccess={(fromISO) => setSuccessFrom(fromISO)}
                onClose={onClose}
                residentDetails={residentDetails}
              />
            )}
          </div>
        )}
      </Modal>
    )
  )
}
